import { TEST_CDK_URL } from '@/constants/constants';
import { PaginationResponse } from '@/hooks/usePaginationFilter';
import { TBuild, TypeOfBuild } from '@/types/build.type';
import { apiFetch } from '@/utils/apiWrapper';
import { formatUrlWithQueryParams } from '@/utils/helpers/formatUrlWithFilters';

const buildURL = `${TEST_CDK_URL}build`;
// Available query-string parameters for the "List Builds by Company" endpoint.
// All fields are optional.
export interface BuildFilterDto {
  offset?: number;
  limit?: number;
  isPublished?: boolean;
  buildType?: TypeOfBuild;
  globalBlockType?: string;
  organizationId?: string;
  tag?: string;
  search?: string;
  page?: number;
  includeNeverPublishedBuilds?: boolean;
}

// Define the CreateBuildDto interface
export interface CreateBuildDto {
  name: string;
  companyId: string;
  buildType?: TypeOfBuild;
  description?: string;
  previewImage?: string;
  isPublished?: boolean;
  tag?: string;
}

export async function findBuildsByQuery(
  companyId: string,
  organizationId: string,
  filters?: BuildFilterDto,
): Promise<PaginationResponse<TBuild>> {
  try {
    console.log('organizationId', organizationId);
    let url = `${buildURL}/company/${organizationId}/${companyId}`;

    if (filters) {
      const { page, limit = 20, ...restFilters } = filters;

      const offsetFromPage = page !== undefined && page > 0 ? (page - 1) * limit : undefined;

      const queryParams = {
        limit,
        ...restFilters,
        ...(restFilters.offset === undefined && offsetFromPage !== undefined
          ? { offset: offsetFromPage }
          : {}),
      } as Record<string, unknown>;

      url = formatUrlWithQueryParams(url, queryParams);
    }

    const response = await apiFetch(url);

    if (!response) {
      throw new Error('Error fetching builds');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching builds by query:', error);
    throw error;
  }
}

export async function getBuildById(organizationId: string, id: string): Promise<TBuild> {
  try {
    const response = await apiFetch(`${buildURL}/${organizationId}/${id}`);
    if (!response?.ok) {
      throw new Error(`Error fetching build by ID: ${response?.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching build by ID:', error);
    throw error;
  }
}
/**
 * Create a new build
 * @param buildData The build data to create
 * @returns The created build
 */
export async function createBuild(
  organizationId: string,
  buildData: Partial<TBuild>,
): Promise<TBuild> {
  try {
    const response = await apiFetch(`${buildURL}/${organizationId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(buildData),
    });

    if (!response?.ok) {
      throw new Error(`Error creating build: ${response?.statusText}`);
    }

    return (await response.json()) as TBuild;
  } catch (error) {
    console.error('Error creating build:', error);
    throw error;
  }
}

export async function updateBuild(
  buildId: string,
  organizationId: string,
  updates: Partial<TBuild>,
): Promise<TBuild> {
  try {
    const response = await apiFetch(`${buildURL}/${organizationId}/${buildId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    if (!response?.ok) {
      throw new Error(`Error updating build: ${response?.statusText}`);
    }

    return (await response.json()) as TBuild;
  } catch (error) {
    console.error('Error updating build:', error);
    throw error;
  }
}

export async function deleteBuild(organizationId: string, buildId: string): Promise<TBuild> {
  try {
    const response = await apiFetch(`${buildURL}/${organizationId}/${buildId}`, {
      method: 'DELETE',
    });
    if (!response?.ok) {
      throw new Error(`Error deleting build: ${response?.statusText}`);
    }
    return (await response.json()) as TBuild;
  } catch (error) {
    console.error('Error deleting build:', error);
    throw error;
  }
}
